import { getCardSelectionState } from "./selectionState.js";

export const ENTRY_AVAILABILITY_KEY = "ww_entry_availability";
export const ENTRY_AVAILABILITY_TTL_MS = 3 * 60 * 60 * 1000;
export const SELECTED_STORAGE_KEY = "selected";
const IMAGE_STORAGE_MAX_LENGTH = 2048;

const SELECTED_STORAGE_FIELDS = [
  "athlete_id",
  "stat_id",
  "term_id",
  "uid",
  "name",
  "stat_display",
  "stat_key",
  "stat",
  "pp_stat",
  "selected",
  "team",
  "team_short",
  "opponent_short",
  "game_id",
  "start_time",
  "start_time_iso",
  "start_time_obj",
  "result_time",
  "position",
  "location",
  "league",
  "og_value",
  "src",
  "last_updated",
  "board_bundle_id",
  "board_snapshot_id",
  "board_observed_at",
  "lineup",
  "free_square",
  "freeSquare",
  "is_free_square",
  // Required at checkout: live selections have a past scheduled start by
  // definition, so the server must receive this state for its fresh-line
  // validation rather than treating the pick as an expired pregame offer.
  "is_live",
];

const TEAM_LINEUP_STORAGE_FIELDS = [
  "key",
  "eventKey",
  "sport",
  "teamKey",
  "teamName",
  "opponentKey",
  "opponentName",
  "startTime",
];

const getLocalStorage = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage || null;
  } catch (error) {
    return null;
  }
};

const assignPresentFields = (target, source, fields) => {
  fields.forEach((field) => {
    if (source[field] !== undefined && source[field] !== null && source[field] !== "") {
      target[field] = source[field];
    }
  });
};

const compactTeamLineupMetaForStorage = (teamLineup) => {
  if (!teamLineup || typeof teamLineup !== "object") {
    return null;
  }

  const compact = {};
  assignPresentFields(compact, teamLineup, TEAM_LINEUP_STORAGE_FIELDS);

  return Object.keys(compact).length > 0 ? compact : null;
};

const getCompactImageForStorage = (image) => {
  const value = typeof image === "string" ? image.trim() : "";
  if (!value || value.length > IMAGE_STORAGE_MAX_LENGTH || value.startsWith("data:")) {
    return "";
  }

  return value;
};

export const compactSelectedAthleteForStorage = (athlete) => {
  if (!athlete || typeof athlete !== "object") {
    return athlete;
  }

  const {
    _boardRefreshState,
    _boardRefreshToken,
    _boardPreviousStat,
    _selectionSyncStatus,
    _selectionCurrentStat,
    _selectionCurrentStatDisplay,
    alt_stats,
    image,
    opponent_logo,
    team_color,
    team_color_two,
    team_logo,
    ud_odds,
    ...source
  } = athlete;
  const compact = {};

  assignPresentFields(compact, source, SELECTED_STORAGE_FIELDS);
  compact.easy = Boolean(source.easy);
  compact.hard = Boolean(source.hard);
  compact.promo = Boolean(source.promo);

  const compactImage = getCompactImageForStorage(image);
  if (compactImage) {
    compact.image = compactImage;
  }

  const teamLineup = compactTeamLineupMetaForStorage(source.teamLineup);
  if (teamLineup) {
    compact.teamLineup = teamLineup;
  }

  return compact;
};

export const compactSelectedAthletesForStorage = (selected) =>
  (Array.isArray(selected) ? selected : []).map(compactSelectedAthleteForStorage);

export const readSelectedAthletesFromStorage = ({ storageKey = SELECTED_STORAGE_KEY } = {}) => {
  const storage = getLocalStorage();
  if (!storage) {
    return [];
  }

  try {
    const rawSelected = storage.getItem(storageKey);
    if (!rawSelected) {
      return [];
    }

    const parsedSelected = JSON.parse(rawSelected);
    return Array.isArray(parsedSelected) ? parsedSelected : [];
  } catch (error) {
    try {
      storage.removeItem(storageKey);
    } catch (removeError) {
      // no-op
    }
    return [];
  }
};

export const writeSelectedAthletesToStorage = (
  selected,
  { freeStorage, storageKey = SELECTED_STORAGE_KEY } = {}
) => {
  const storage = getLocalStorage();
  if (!storage) {
    return false;
  }

  const selectedForStorage = compactSelectedAthletesForStorage(selected);
  const serializedSelected = JSON.stringify(selectedForStorage);

  try {
    storage.setItem(storageKey, serializedSelected);
    return true;
  } catch (error) {
    if (typeof freeStorage === "function") {
      try {
        freeStorage(error);
      } catch (freeStorageError) {
        // no-op
      }
    }

    try {
      storage.setItem(storageKey, serializedSelected);
      return true;
    } catch (retryError) {
      try {
        storage.removeItem(storageKey);
      } catch (removeError) {
        // no-op
      }
      return false;
    }
  }
};

export const removeSelectedAthletesFromStorage = () => {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(SELECTED_STORAGE_KEY);
  } catch (error) {
    // no-op
  }
};

export const getEntryAvailabilityState = () => {
  try {
    const raw = localStorage.getItem(ENTRY_AVAILABILITY_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== "number") {
      return null;
    }

    if (Date.now() - parsed.ts > ENTRY_AVAILABILITY_TTL_MS) {
      localStorage.removeItem(ENTRY_AVAILABILITY_KEY);
      return null;
    }

    return parsed;
  } catch (error) {
    return null;
  }
};

export const setEntryAvailabilityState = (removedSelections = []) => {
  const now = Date.now();
  const existingState = getEntryAvailabilityState();
  const existingSelections = getUnavailableSelections(existingState);
  const mergedSelections = new Map();

  existingSelections.forEach((selection) => {
    const selectionKey = getSelectionAvailabilityKey(selection);
    if (selectionKey) {
      mergedSelections.set(selectionKey, {
        ...selection,
        _ww_unavailable_at: Number(selection?._ww_unavailable_at || existingState?.ts || now),
      });
    }
  });
  (Array.isArray(removedSelections) ? removedSelections : []).forEach((selection) => {
    const selectionKey = getSelectionAvailabilityKey(selection);
    if (selectionKey) {
      mergedSelections.set(selectionKey, { ...selection, _ww_unavailable_at: now });
    }
  });

  localStorage.setItem(
    ENTRY_AVAILABILITY_KEY,
    JSON.stringify({
      ts: now,
      removed: Array.from(mergedSelections.values()),
    })
  );
};

export const getUnavailableSelections = (availabilityState = getEntryAvailabilityState()) => {
  if (!availabilityState || typeof availabilityState !== "object") {
    return [];
  }

  const selections = Array.isArray(availabilityState.removed)
    ? availabilityState.removed
    : Array.isArray(availabilityState.selections)
      ? availabilityState.selections
      : Array.isArray(availabilityState.selected)
        ? availabilityState.selected
        : [];
  const fallbackTimestamp = Number(availabilityState.ts || 0);

  return selections.filter((selection) => {
    const unavailableAt = Number(selection?._ww_unavailable_at || fallbackTimestamp);
    return unavailableAt > 0 && Date.now() - unavailableAt <= ENTRY_AVAILABILITY_TTL_MS;
  });
};

export const getSelectionAvailabilityKey = (selection) => {
  const selectionState = getCardSelectionState(
    selection?.stat_key,
    selection?.promo,
    selection?.easy,
    selection?.hard
  );
  const normalizedStatKey = selectionState.statKey;
  if (
    !selection ||
    selection.athlete_id === undefined ||
    selection.athlete_id === null ||
    selection.athlete_id === "" ||
    !normalizedStatKey
  ) {
    return "";
  }

  return `${selection.athlete_id}|${normalizedStatKey}|${selectionState.promoToken}`;
};

export const getUnavailableSelectionSet = () => {
  const unavailableSelections = new Set();

  getUnavailableSelections().forEach((selection) => {
    const selectionKey = getSelectionAvailabilityKey(selection);
    if (selectionKey) {
      unavailableSelections.add(selectionKey);
    }
  });

  return unavailableSelections;
};

export const filterUnavailableSelections = (list) => {
  const normalizedList = Array.isArray(list) ? list : [];
  const unavailableSelections = getUnavailableSelectionSet();
  if (unavailableSelections.size === 0) {
    return normalizedList;
  }

  return normalizedList.filter(
    (selection) => !unavailableSelections.has(getSelectionAvailabilityKey(selection))
  );
};
